import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Plus, Trash2, Award, CheckCircle2, XCircle, BookOpen, Rocket, Send, AlertTriangle, Clock, ExternalLink, FileText } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const STATE_MAP: Record<string, string> = {
  assigned: "info", in_progress: "warn", overdue: "critical", completed: "good",
  locked: "inactive", recertify: "critical",
};
const STATE_LABEL: Record<string, string> = {
  assigned: "Assigned", in_progress: "In progress", overdue: "Overdue", completed: "Completed",
  locked: "Prerequisite required", recertify: "Recertify",
};
const CERT_MAP: Record<string, string> = { valid: "good", expiring: "warn", expired: "critical", revoked: "critical", superseded: "inactive" };

export default function Learning() {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const canAuthor = hasPerm("learn.author");
  const canAssign = hasPerm("learn.assign");
  const [tab, setTab] = useState("assigned");
  const [assignments, setAssignments] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [takeId, setTakeId] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/learn/assignments/assigned").then((r) => setAssignments(r.data.assignments)),
      api.get("/learn/notifications").then((r) => setNotes(r.data.notifications)).catch(() => {}),
      (canAuthor || canAssign) ? api.get("/learn/courses").then((r) => setCourses(r.data.courses)) : Promise.resolve(),
    ]).finally(() => setLoading(false));
  }, [canAuthor, canAssign]);
  useEffect(() => { load(); }, [load]);

  return (
    (<div>
      <PageHeader title="Learning & Certification" subtitle="Assigned courses, assessments and certifications" testId="learning-header"
        actions={canAuthor && <Button onClick={() => setComposeOpen(true)} data-testid="learn-new-course"><Plus size={16} className="mr-1.5" />{t("New course")}</Button>} />
      {notes.length > 0 && (
        <div className="mb-4 rounded-lg border border-status-warn/40 bg-status-warn/10 p-3" data-testid="learn-notifications">
          <div className="flex items-center gap-2 text-sm font-semibold text-status-warn"><AlertTriangle size={15} />{t("You have")}{" "}{notes.length}{" "}{t("learning reminder(s)")}</div>
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {notes.map((n, i) => <li key={i} data-testid={`note-${i}`}>{n.type === "overdue" ? `${tl("Overdue:")} ${n.title}` : `${tl("Certification")} ${n.type.replace("cert_", "")}: ${n.title}`}</li>)}
          </ul>
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="assigned" data-testid="learn-tab-assigned">{t("My Learning")}</TabsTrigger>
          {(canAuthor || canAssign) && <TabsTrigger value="library" data-testid="learn-tab-library">{t("Course Library")}</TabsTrigger>}
          {canAssign && <TabsTrigger value="exceptions" data-testid="learn-tab-exceptions">{t("Exceptions")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="assigned">
          {loading ? <LoadingBlock /> : assignments.length === 0 ? (
            <EmptyState icon={<GraduationCap size={28} />} title="No courses assigned" description="Assigned courses will appear here." />
          ) : (
            <div className="grid gap-3 md:grid-cols-2" data-testid="learn-assigned-list">
              {assignments.map((a) => (
                <Card key={a.assignment_id} className="p-4" data-testid={`assignment-${a.assignment_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 font-display font-semibold"><BookOpen size={16} className="text-brand" />{a.title}</div>
                    <StatusPill status={STATE_MAP[a.state] || "info"} testId={`state-${a.assignment_id}`}>{STATE_LABEL[a.state] || a.state}</StatusPill>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{stat(a.category)}{" "}{t("· v")}{a.version_no}</span>
                    {a.required ? <StatusPill status="warn">{t("Required")}</StatusPill> : <span>{t("Optional")}</span>}
                    {a.due_date && <span className="flex items-center gap-1"><Clock size={11} />{t("due")}{" "}{a.due_date}</span>}
                    {a.attempts ? <span>· {a.attempts}{" "}{t("attempt(s)")}</span> : null}
                  </div>
                  {a.certification_enabled && a.cert_status && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs">
                      <Award size={13} className="text-brand" />
                      <StatusPill status={CERT_MAP[a.cert_status] || "info"} testId={`cert-${a.assignment_id}`}>{stat(a.cert_status)}{a.cert_expires_at ? ` · ${tl("exp")} ${a.cert_expires_at.slice(0, 10)}` : ""}</StatusPill>
                    </div>
                  )}
                  <Button size="sm" className="mt-3" disabled={a.state === "locked"} onClick={() => setTakeId(a.assignment_id)} data-testid={`take-course-${a.assignment_id}`}>
                    {a.state === "locked" ? t("Locked") : a.completed ? t("Review / Retake") : t("Start course")}
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {(canAuthor || canAssign) && (
          <TabsContent value="library">
            {courses.length === 0 ? <EmptyState icon={<GraduationCap size={28} />} title="No courses" description="Create a course to get started." /> : (
              <div className="grid gap-3 md:grid-cols-2" data-testid="learn-library-list">
                {courses.map((c) => <CourseCard key={c.id} course={c} canAssign={canAssign} onChange={load} />)}
              </div>
            )}
          </TabsContent>
        )}

        {canAssign && <TabsContent value="exceptions"><ExceptionsPanel /></TabsContent>}
      </Tabs>
      {takeId && <TakeCourseDialog assignmentId={takeId} onClose={() => setTakeId(null)} onDone={load} />}
      {canAuthor && <ComposeDialog open={composeOpen} setOpen={setComposeOpen} onDone={load} />}
    </div>)
  );
}

const ExceptionsPanel: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/learn/exceptions").then((r) => setRows(r.data.exceptions)).finally(() => setLoading(false)); }, []);
  if (loading) return <LoadingBlock />;
  if (rows.length === 0) return <EmptyState icon={<CheckCircle2 size={28} />} title="No exceptions" description="No overdue or expiring learning in your scope." />;
  return (
    <div className="space-y-2" data-testid="exceptions-list">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`exception-${i}`}>
          <div><div className="text-sm font-medium">{r.user_name}</div><div className="text-xs text-muted-foreground">{r.course_title}{r.due_date ? ` · ${tl("due")} ${r.due_date}` : ""}</div></div>
          <div className="flex gap-2">
            <StatusPill status={STATE_MAP[r.state] || "info"}>{STATE_LABEL[r.state] || r.state}</StatusPill>
            {r.cert_status && <StatusPill status={CERT_MAP[r.cert_status] || "info"}>{stat(r.cert_status)}</StatusPill>}
          </div>
        </div>
      ))}
    </div>
  );
};

const CourseCard: React.FC<{ course: any; canAssign: boolean; onChange: () => void }> = ({ course, canAssign, onChange }) => {
  const {
    t
  } = useI18n();

  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("tenant");
  const [required, setRequired] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [stores, setStores] = useState<any[]>([]);
  useEffect(() => { if (open) api.get("/org/stores").then((r) => setStores(r.data.stores)).catch(() => {}); }, [open]);
  const assign = async () => {
    const audience: any = scope === "tenant" ? { scope_type: "tenant" } : { scope_type: "store", scope_id: scope };
    try { const r = await api.post(`/learn/courses/${course.id}/assign`, { ...audience, required, due_date: dueDate || null }); toast.success(tl("Assigned to {n} people", { n: r.data.recipient_count })); setOpen(false); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };
  return (
    (<Card className="p-4" data-testid={`course-${course.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 font-display font-semibold"><GraduationCap size={16} className="text-brand" />{course.title}</div>
        <StatusPill status={course.status === "published" ? "good" : course.status === "draft" ? "info" : "inactive"}>{stat(course.status)}</StatusPill>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{stat(course.category)}{" "}{t("· v")}{course.current_version_no || 0}{course.estimated_minutes ? ` · ~${course.estimated_minutes}m` : ""}</div>
      {canAssign && course.status === "published" && (
        <Button size="sm" variant="outline" className="mt-3" onClick={() => setOpen(true)} data-testid={`assign-course-${course.id}`}>{t("Assign")}</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>{t("Assign “")}{course.title}”</DialogTitle><DialogDescription>{t("Choose audience, requirement and due date.")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("Audience")}</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="mt-1" data-testid="assign-audience"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tenant">{t("Entire organization")}</SelectItem>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{t("Store")}{" "}{s.store_number}· {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("Due date")}</Label><Input type="date" className="mt-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="assign-due-date" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={required} onCheckedChange={setRequired} data-testid="assign-required" />{t("Required learning")}</label>
          </div>
          <DialogFooter><Button onClick={assign} data-testid="assign-confirm"><Send size={15} className="mr-1.5" />{t("Assign")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>)
  );
};

const TakeCourseDialog: React.FC<{ assignmentId: string; onClose: () => void; onDone: () => void }> = ({ assignmentId, onClose, onDone }) => {
  const {
    t
  } = useI18n();

  const [content, setContent] = useState<any>(null);
  const [viewed, setViewed] = useState<Set<string>>(new Set());
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/learn/assignments/${assignmentId}/learn`)
      .then((r) => { setContent(r.data); setViewed(new Set(r.data.viewed_lessons || [])); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
  }, [assignmentId]);

  const markLesson = async (lid: string) => {
    if (viewed.has(lid)) return;
    try { await api.post(`/learn/assignments/${assignmentId}/lesson/${lid}/complete`, {}); setViewed((p) => new Set([...p, lid])); } catch { /* noop */ }
  };

  const setMulti = (qid: string, oi: number) => setAnswers((p) => {
    const cur: number[] = Array.isArray(p[qid]) ? p[qid] : [];
    return { ...p, [qid]: cur.includes(oi) ? cur.filter((x) => x !== oi) : [...cur, oi] };
  });

  const submit = async () => {
    setBusy(true);
    try { const r = await api.post(`/learn/assignments/${assignmentId}/submit`, { answers }); setResult(r.data); onDone(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const questions = content?.quiz?.questions || [];
  return (
    (<Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="take-course-dialog">
        <DialogHeader><DialogTitle>{content?.title || "Course"}</DialogTitle><DialogDescription>{t("Read each lesson, then complete the assessment.")}</DialogDescription></DialogHeader>
        {!content ? <LoadingBlock /> : result ? (
          result.status === "pending_review" ? (
            <div className="py-6 text-center" data-testid="quiz-pending">
              <Clock size={48} className="mx-auto text-status-warn" />
              <div className="mt-3 font-display text-xl font-bold">{t("Submitted for review")}</div>
              <div className="text-sm text-muted-foreground">{t("A manager will review your response.")}</div>
              <Button className="mt-4" onClick={onClose} data-testid="quiz-close">{t("Done")}</Button>
            </div>
          ) : (
            <div className="py-6 text-center" data-testid="quiz-result">
              {result.passed ? <CheckCircle2 size={48} className="mx-auto text-status-good" /> : <XCircle size={48} className="mx-auto text-status-critical" />}
              <div className="mt-3 font-display text-2xl font-bold">{result.score}%</div>
              <div className="text-sm text-muted-foreground">{result.passed ? t("Passed") : tl("Not passed (need {pct}%)", { pct: result.pass_threshold })}{" "}{t("· attempt")}{" "}{result.attempt_no}</div>
              {result.certification && <div className="mt-2 flex items-center justify-center gap-1.5 text-sm text-brand"><Award size={16} />{t("Certification")}{" "}{result.certification.event}{result.certification.expires_at ? ` · ${tl("expires")} ${result.certification.expires_at.slice(0, 10)}` : ""}</div>}
              <div className="mt-4 flex justify-center gap-2">
                {!result.passed && <Button variant="outline" onClick={() => { setResult(null); setAnswers({}); }} data-testid="quiz-retake">{t("Retake")}</Button>}
                <Button onClick={onClose} data-testid="quiz-close">{t("Done")}</Button>
              </div>
            </div>
          )
        ) : (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto">
            {content.modules.map((m: any, mi: number) => (
              <div key={m.module_id} data-testid={`module-${mi}`}>
                <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{m.title}</div>
                <div className="space-y-2">
                  {m.lessons.map((l: any, li: number) => (
                    <div key={l.lesson_id} className="rounded-md border border-border p-3" data-testid={`lesson-${mi}-${li}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-display font-semibold">{l.title}</div>
                        {viewed.has(l.lesson_id) ? <CheckCircle2 size={16} className="text-status-good" /> :
                          <Button size="sm" variant="outline" onClick={() => markLesson(l.lesson_id)} data-testid={`lesson-complete-${l.lesson_id}`}>{t("Mark read")}</Button>}
                      </div>
                      {l.content_type === "text" && <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{l.body}</p>}
                      {l.content_type === "knowledge" && <a className="mt-1 inline-flex items-center gap-1 text-sm text-brand hover:underline" href={`/knowledge`} data-testid={`lesson-kdoc-${l.lesson_id}`}><FileText size={13} />{l.knowledge_doc_title || "Knowledge document"}</a>}
                      {l.content_type === "image" && l.image_url && <img src={l.image_url} alt={l.title} className="mt-2 max-h-48 rounded" />}
                      {l.content_type === "link" && l.link_url && <a className="mt-1 inline-flex items-center gap-1 text-sm text-brand hover:underline" href={l.link_url} target="_blank" rel="noreferrer"><ExternalLink size={13} />{l.link_is_video ? t("Watch video") : t("Open link")}</a>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("Assessment · pass")}{" "}{content.quiz.pass_threshold}%{content.quiz.attempt_limit ? ` · ${content.quiz.attempt_limit} attempt(s)` : ""}</div>
              <div className="space-y-3">
                {questions.map((q: any, qi: number) => (
                  <div key={q.q_id} className="rounded-md border border-border p-3" data-testid={`question-${qi}`}>
                    <div className="text-sm font-medium">{qi + 1}. {q.text} {q.type === "multiple" && <span className="text-[11px] text-muted-foreground">{t("(select all)")}</span>}</div>
                    <div className="mt-2 space-y-1.5">
                      {q.type === "short_answer" ? (
                        <Textarea value={answers[q.q_id] || ""} onChange={(e) => setAnswers((p) => ({ ...p, [q.q_id]: e.target.value }))} placeholder={t("Your answer")} data-testid={`q${qi}-text`} />
                      ) : q.options.map((opt: string, oi: number) => {
                        const selected = q.type === "multiple" ? (Array.isArray(answers[q.q_id]) && answers[q.q_id].includes(oi)) : answers[q.q_id] === oi;
                        return (
                          <label key={oi} className={`flex cursor-pointer items-center gap-2 rounded border p-2 text-sm ${selected ? "border-brand bg-brand/10" : "border-border"}`} data-testid={`q${qi}-opt${oi}`}
                            onClick={() => q.type === "multiple" ? setMulti(q.q_id, oi) : setAnswers((p) => ({ ...p, [q.q_id]: oi }))}>
                            {q.type === "multiple" ? <Checkbox checked={selected} /> : <span className={`h-3 w-3 rounded-full border ${selected ? "border-brand bg-brand" : "border-muted-foreground"}`} />}
                            {opt}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter><Button onClick={submit} disabled={busy} data-testid="quiz-submit"><Send size={15} className="mr-1.5" />{t("Submit assessment")}</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>)
  );
};

const ComposeDialog: React.FC<{ open: boolean; setOpen: (v: boolean) => void; onDone: () => void }> = ({ open, setOpen, onDone }) => {
  const {
    t
  } = useI18n();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [lessons, setLessons] = useState([{ title: "", body: "" }]);
  const [questions, setQuestions] = useState<any[]>([{ type: "single", text: "", options: ["", ""], correct: [0] }]);
  const [threshold, setThreshold] = useState(80);
  const [attemptLimit, setAttemptLimit] = useState("");
  const [certEnabled, setCertEnabled] = useState(false);
  const [validity, setValidity] = useState("365");
  const [busy, setBusy] = useState(false);

  const reset = () => { setTitle(""); setDescription(""); setLessons([{ title: "", body: "" }]); setQuestions([{ type: "single", text: "", options: ["", ""], correct: [0] }]); setThreshold(80); setAttemptLimit(""); setCertEnabled(false); setValidity("365"); };

  const publish = async () => {
    if (!title) { toast.error("Title required"); return; }
    const goodLessons = lessons.filter((l) => l.title.trim());
    if (goodLessons.length === 0) { toast.error("Add at least one lesson"); return; }
    const goodQ = questions.filter((q) => q.text.trim() && (q.type === "short_answer" || q.type === "true_false" || q.options.filter((o: string) => o.trim()).length >= 2));
    if (goodQ.length === 0) { toast.error("Add at least one valid question"); return; }
    setBusy(true);
    try {
      const cr = await api.post("/learn/courses", { title, description, category: "General" });
      const cid = cr.data.id;
      await api.put(`/learn/courses/${cid}/draft`, {
        modules: [{ title: "Module 1", lessons: goodLessons.map((l) => ({ title: l.title, content_type: "text", body: l.body })) }],
        quiz: { pass_threshold: threshold, attempt_limit: attemptLimit ? parseInt(attemptLimit) : null, questions: goodQ.map((q) => ({ type: q.type, text: q.text, options: q.type === "true_false" ? [] : q.options.filter((o: string) => o.trim()), correct: q.type === "short_answer" ? [] : q.correct })) },
        certification: { enabled: certEnabled, validity_days: certEnabled ? parseInt(validity) || null : null },
      });
      await api.post(`/learn/courses/${cid}/publish`, {});
      toast.success("Course published");
      setOpen(false); reset(); onDone();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setQ = (qi: number, patch: any) => setQuestions((p) => p.map((x, j) => j === qi ? { ...x, ...patch } : x));

  return (
    (<Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="learn-compose-dialog">
        <DialogHeader><DialogTitle>{t("New course")}</DialogTitle><DialogDescription>{t("Add lessons and a graded assessment, then publish.")}</DialogDescription></DialogHeader>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto">
          <div><Label>{t("Title")}</Label><Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="course-title" /></div>
          <div><Label>{t("Description")}</Label><Textarea className="mt-1" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="course-description" /></div>

          <div>
            <Label>{t("Lessons")}</Label>
            {lessons.map((l, i) => (
              <div key={i} className="mt-1 flex gap-2">
                <div className="flex-1 space-y-1">
                  <Input placeholder={tl("Lesson {n} title", { n: i + 1 })} value={l.title} onChange={(e) => setLessons((p) => p.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} data-testid={`lesson-title-${i}`} />
                  <Textarea placeholder={t("Lesson content")} value={l.body} onChange={(e) => setLessons((p) => p.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} data-testid={`lesson-body-${i}`} />
                </div>
                {lessons.length > 1 && <Button variant="outline" size="icon" onClick={() => setLessons((p) => p.filter((_, j) => j !== i))}><Trash2 size={15} /></Button>}
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setLessons((p) => [...p, { title: "", body: "" }])} data-testid="add-lesson"><Plus size={14} className="mr-1" />{t("Add lesson")}</Button>
          </div>

          <div>
            <Label>{t("Assessment questions")}</Label>
            {questions.map((q, qi) => (
              <div key={qi} className="mt-1 rounded-md border border-border p-2" data-testid={`compose-question-${qi}`}>
                <div className="flex gap-2">
                  <Input placeholder={tl("Question {n}", { n: qi + 1 })} value={q.text} onChange={(e) => setQ(qi, { text: e.target.value })} data-testid={`compose-q-text-${qi}`} />
                  <Select value={q.type} onValueChange={(v) => setQ(qi, { type: v, correct: v === "multiple" ? [] : [0] })}>
                    <SelectTrigger className="w-36" data-testid={`compose-q-type-${qi}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">{t("Single")}</SelectItem><SelectItem value="multiple">{t("Multiple")}</SelectItem>
                      <SelectItem value="true_false">{t("True/False")}</SelectItem><SelectItem value="short_answer">{t("Short answer")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {q.type !== "short_answer" && q.type !== "true_false" && (
                  <div className="mt-2 space-y-1">
                    {q.options.map((opt: string, oi: number) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input type={q.type === "multiple" ? t("checkbox") : t("radio")} checked={q.correct.includes(oi)}
                          onChange={() => setQ(qi, { correct: q.type === "multiple" ? (q.correct.includes(oi) ? q.correct.filter((c: number) => c !== oi) : [...q.correct, oi]) : [oi] })} data-testid={`compose-q${qi}-correct-${oi}`} />
                        <Input placeholder={tl("Option {n}", { n: oi + 1 })} value={opt} onChange={(e) => setQ(qi, { options: q.options.map((o: string, k: number) => k === oi ? e.target.value : o) })} data-testid={`compose-q${qi}-opt-${oi}`} />
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" onClick={() => setQ(qi, { options: [...q.options, ""] })} data-testid={`compose-q${qi}-add-opt`}>{t("+ option")}</Button>
                  </div>
                )}
                {q.type === "true_false" && <div className="mt-2 text-xs text-muted-foreground">{t("Correct:")}{" "}<button className={q.correct[0] === 0 ? t("font-bold text-brand") : ""} onClick={() => setQ(qi, { correct: [0] })} data-testid={`compose-q${qi}-tf-true`}>{t("True")}</button>/ <button className={q.correct[0] === 1 ? t("font-bold text-brand") : ""} onClick={() => setQ(qi, { correct: [1] })} data-testid={`compose-q${qi}-tf-false`}>{t("False")}</button></div>}
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setQuestions((p) => [...p, { type: "single", text: "", options: ["", ""], correct: [0] }])} data-testid="add-question"><Plus size={14} className="mr-1" />{t("Add question")}</Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("Pass threshold %")}</Label><Input type="number" className="mt-1" value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value) || 0)} data-testid="pass-threshold" /></div>
            <div><Label>{t("Attempt limit")}</Label><Input type="number" className="mt-1" value={attemptLimit} placeholder={t("unlimited")} onChange={(e) => setAttemptLimit(e.target.value)} data-testid="attempt-limit" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={certEnabled} onCheckedChange={setCertEnabled} data-testid="cert-enabled" />{t("Issue a certification on pass")}</label>
          {certEnabled && <div><Label>{t("Cert validity (days)")}</Label><Input type="number" className="mt-1" value={validity} onChange={(e) => setValidity(e.target.value)} data-testid="cert-validity" /></div>}
        </div>
        <DialogFooter><Button onClick={publish} disabled={busy} data-testid="course-publish"><Rocket size={15} className="mr-1.5" />{t("Publish course")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};
